import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Box,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  MenuItem,
  Alert,
  Tabs,
  Tab,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Cancel as CancelIcon } from '@mui/icons-material';
import Layout from '../../components/Layout';
import api from '../../services/api';

const CustomerOrders = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [formData, setFormData] = useState({
    job_location: '',
    delivery_notes: '',
    diesel_quantity: '',
    def_quantity: '',
    frequency: 'one-time',
    scheduled_date: '',
  });
  const [saving, setSaving] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const response = await api.get('/customers/profile/jobs/');
      setJobs(response.data);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
    setEditingOrder(null);
    setFormData({
      job_location: '',
      delivery_notes: '',
      diesel_quantity: '',
      def_quantity: '',
      frequency: 'one-time',
      scheduled_date: '',
    });
    setMessage('');
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setOpenEditDialog(false);
    setEditingOrder(null);
  };

  const handleEditOrder = (order) => {
    setEditingOrder(order);
    // Format scheduled_date for datetime-local input
    let formattedDate = '';
    if (order.scheduled_date) {
      const date = new Date(order.scheduled_date);
      // Convert to local datetime string format (YYYY-MM-DDTHH:mm)
      formattedDate = date.toISOString().slice(0, 16);
    }
    
    setFormData({
      job_location: order.job_location || '',
      delivery_notes: order.delivery_notes || '',
      diesel_quantity: order.diesel_quantity || '',
      def_quantity: order.def_quantity || '',
      frequency: order.frequency || 'one-time',
      scheduled_date: formattedDate,
    });
    setOpenEditDialog(true);
    setMessage('');
  };

  const handleCancelOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to cancel this order?')) {
      return;
    }

    setCancelling(true);
    setMessage('');

    try {
      await api.post(`/jobs/${orderId}/cancel/`);
      setMessage('Order cancelled successfully!');
      fetchJobs();
    } catch (error) {
      const errorMsg = error.response?.data?.error || 
                      error.response?.data?.message || 
                      'Error cancelling order.';
      setMessage(errorMsg);
      alert(errorMsg);
    } finally {
      setCancelling(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');

    try {
      const       orderData = {
        job_location: formData.job_location,
        delivery_notes: formData.delivery_notes || '',
        diesel_quantity: parseFloat(formData.diesel_quantity) || 0,
        def_quantity: parseFloat(formData.def_quantity) || 0,
        frequency: formData.frequency,
      };

      if (formData.scheduled_date) {
        orderData.scheduled_date = formData.scheduled_date;
      }

      if (editingOrder) {
        // Update existing order
        await api.patch(`/jobs/${editingOrder.id}/`, orderData);
        setMessage('Order updated successfully!');
      } else {
        // Create new order
        await api.post('/jobs/', orderData);
        setMessage('Order created successfully!');
      }
      
      fetchJobs();
      setTimeout(() => {
        handleCloseDialog();
        setMessage('');
      }, 1500);
    } catch (error) {
      const errorMsg = error.response?.data?.error || 
                      error.response?.data?.message || 
                      `Error ${editingOrder ? 'updating' : 'creating'} order. Please check your input.`;
      setMessage(errorMsg);
      console.error(`Error ${editingOrder ? 'updating' : 'creating'} order:`, error.response?.data || error);
    } finally {
      setSaving(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      accepted: 'info',
      active: 'primary',
      complete: 'success',
      cancelled: 'error',
    };
    return colors[status] || 'default';
  };

  if (loading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  const activeJobs = jobs.filter((j) => ['pending', 'accepted', 'active'].includes(j.job_status));
  const completedJobs = jobs.filter((j) => j.job_status === 'complete');
  const displayedJobs = tabValue === 0 ? activeJobs : completedJobs;

  return (
    <Layout>
      <Container>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h4">
            My Orders
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenDialog}
          >
            Create Order
          </Button>
        </Box>

        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 2 }}>
          <Tab label={`Active Orders (${activeJobs.length})`} />
          <Tab label={`Completed Orders (${completedJobs.length})`} />
        </Tabs>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Order ID</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Quantity</TableCell>
                <TableCell>Frequency</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Payment Status</TableCell>
                <TableCell>Driver</TableCell>
                <TableCell>Vehicle</TableCell>
                <TableCell>Invoice #</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedJobs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    <Typography variant="body2" color="textSecondary">
                      No {tabValue === 0 ? 'active' : 'completed'} orders found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                displayedJobs.map((job) => {
                  const canEdit = job.job_status === 'pending';
                  const canCancel = ['pending', 'accepted'].includes(job.job_status);
                  
                  return (
                    <TableRow key={job.id}>
                      <TableCell>{job.id.substring(0, 8)}</TableCell>
                      <TableCell>{job.job_location}</TableCell>
                      <TableCell>
                        {job.diesel_quantity > 0 && `${job.diesel_quantity}L Diesel`}
                        {job.diesel_quantity > 0 && job.def_quantity > 0 && ' / '}
                        {job.def_quantity > 0 && `${job.def_quantity}L DEF`}
                        {job.diesel_quantity === 0 && job.def_quantity === 0 && 'N/A'}
                      </TableCell>
                      <TableCell>{job.frequency}</TableCell>
                      <TableCell>
                        <Chip
                          label={job.job_status}
                          color={getStatusColor(job.job_status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={job.payment_status}
                          color={job.payment_status === 'paid' ? 'success' : 'warning'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{job.driver_name || 'Not assigned'}</TableCell>
                      <TableCell>{job.vehicle_info || 'Not assigned'}</TableCell>
                      <TableCell>{job.invoice_number || 'N/A'}</TableCell>
                      <TableCell>{new Date(job.created_at).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <Box display="flex" gap={1}>
                          {canEdit && (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<EditIcon />}
                              onClick={() => handleEditOrder(job)}
                            >
                              Edit
                            </Button>
                          )}
                          {canCancel && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<CancelIcon />}
                              onClick={() => handleCancelOrder(job.id)}
                              disabled={cancelling}
                            >
                              Cancel
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Create/Edit Order Dialog */}
        <Dialog 
          open={openDialog || openEditDialog} 
          onClose={handleCloseDialog} 
          maxWidth="md" 
          fullWidth
        >
          <DialogTitle>{editingOrder ? 'Edit Order' : 'Create New Order'}</DialogTitle>
          <form onSubmit={handleSubmit}>
            <DialogContent>
              {message && (
                <Alert 
                  severity={message.includes('success') ? 'success' : 'error'} 
                  sx={{ mb: 2 }}
                >
                  {message}
                </Alert>
              )}
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Delivery Location"
                    name="job_location"
                    value={formData.job_location}
                    onChange={handleChange}
                    multiline
                    rows={3}
                    required
                    placeholder="Enter the full delivery address"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Delivery Notes"
                    name="delivery_notes"
                    value={formData.delivery_notes}
                    onChange={handleChange}
                    multiline
                    rows={3}
                    placeholder="Enter delivery notes and special instructions"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Diesel Quantity (Liters)"
                    name="diesel_quantity"
                    type="number"
                    value={formData.diesel_quantity}
                    onChange={handleChange}
                    inputProps={{ min: 0, step: 0.01 }}
                    helperText="Enter diesel quantity in liters"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="DEF Quantity (Liters)"
                    name="def_quantity"
                    type="number"
                    value={formData.def_quantity}
                    onChange={handleChange}
                    inputProps={{ min: 0, step: 0.01 }}
                    helperText="Enter DEF quantity in liters"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    select
                    label="Frequency"
                    name="frequency"
                    value={formData.frequency}
                    onChange={handleChange}
                    required
                  >
                    <MenuItem value="one-time">One-time</MenuItem>
                    <MenuItem value="weekly">Weekly</MenuItem>
                    <MenuItem value="bi-weekly">Bi-weekly</MenuItem>
                    <MenuItem value="monthly">Monthly</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Scheduled Date (Optional)"
                    name="scheduled_date"
                    type="datetime-local"
                    value={formData.scheduled_date}
                    onChange={handleChange}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    helperText="Leave empty for immediate delivery"
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog} disabled={saving}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" disabled={saving}>
                {saving 
                  ? (editingOrder ? 'Updating...' : 'Creating...') 
                  : (editingOrder ? 'Update Order' : 'Create Order')
                }
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      </Container>
    </Layout>
  );
};

export default CustomerOrders;



